import { InputField } from 'lib/iterable-hook-form';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { CATEGORY_LIMIT, CATEGORY_LIMIT_TOOLTIP } from 'modules/dragAndDropEditor/constants';
import { MessageCategory } from 'modules/dragAndDropEditor/types';
import { BUTTON_VARIANTS, GhostButton, IconButton } from 'ui/button';
import { FormField } from 'ui/formField';
import { AddNew24, Delete16 } from 'ui/icons';
import { spacing } from 'ui/theme';
import { TipPlacement, Tooltip } from 'ui/tooltip';
import { pxToRem } from 'utils/convert';
import { NoOp } from 'utils/noop';
import { ManageCustomCategoriesFormValues } from './ManageCustomCategoriesFormProvider';
import { MANAGE_CUSTOM_CATEGORIES_FORM_FIELD_NAMES } from './ManageCustomCategoriesModal.constants';
import { CategoriesFieldArray, CategoryFieldToDelete } from './ManageCustomCategoriesModal.types';
import { useCategoriesRowCountTextsMapper } from './hooks/useCategoriesRowCountTextsMapper';

const StyledFormField = styled(FormField)`
  && {
    margin: 0;
  }
`;

const StyledInputField = styled(InputField)`
  flex: auto;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
`;

const AddCategoryButton = styled(GhostButton)`
  display: flex;
  justify-content: start;
  padding: ${spacing.half} ${spacing.threeQuarter};
`;

const BodyWrapper = styled.div`
  max-height: ${pxToRem(310)};
  overflow-y: auto;
  padding: ${spacing.quarter} ${spacing.plusHalf};
  display: flex;
  flex-direction: column;
  gap: ${spacing.threeQuarter};
  justify-content: start;
`;

const StyledTooltip = styled(Tooltip)`
  width: fit-content;
`;

export const MANAGE_CATEGORY_FIELDS_ID = 'manage-category-fields';
export const ADD_CATEGORY_BUTTON_ID = 'add-category';
export const DELETE_CATEGORY_BUTTON_ID = 'delete-category';

type ManageCustomCategoriesFormProps = {
  messageCategories: MessageCategory[];
  fieldArray?: CategoriesFieldArray;
  onDeleteCategory: (category: CategoryFieldToDelete, callback?: NoOp) => void;
};

export const ManageCustomCategoriesForm = ({
  fieldArray,
  messageCategories = [],
  onDeleteCategory,
}: ManageCustomCategoriesFormProps) => {
  const {
    formState: {
      errors: { categories: categoryErrors },
    },
  } = useFormContext<ManageCustomCategoriesFormValues>();

  const rowCountsMapper = useCategoriesRowCountTextsMapper(messageCategories);

  const hasReachedCategoryLimit = fieldArray?.fields
    ? fieldArray.fields?.length >= CATEGORY_LIMIT
    : false;

  return (
    <BodyWrapper data-test={MANAGE_CATEGORY_FIELDS_ID}>
      {fieldArray?.fields.map((field, index: number) => {
        const errorMessage = categoryErrors?.[index]?.name?.message;

        return (
          <StyledFormField errorText={errorMessage} hasError={!!errorMessage} key={field.id}>
            <FieldRow>
              <StyledInputField
                data-test={`category-input-${index}`}
                name={`${MANAGE_CUSTOM_CATEGORIES_FORM_FIELD_NAMES.CATEGORIES}.${index}.name`}
                placeholder={'New category name'}
                Suffix={rowCountsMapper?.[field.name]}
              />
              <IconButton
                data-test={`${DELETE_CATEGORY_BUTTON_ID}-${index}`}
                onClick={() => {
                  onDeleteCategory({
                    ...field,
                    index,
                  });
                }}
                variant={BUTTON_VARIANTS.GHOST}
              >
                <Delete16 />
              </IconButton>
            </FieldRow>
          </StyledFormField>
        );
      })}
      <StyledTooltip
        isEnabled={hasReachedCategoryLimit}
        isUsedOnButton
        placement={TipPlacement.Bottom}
        tooltipContent={CATEGORY_LIMIT_TOOLTIP}
      >
        <AddCategoryButton
          data-test={ADD_CATEGORY_BUTTON_ID}
          isDisabled={hasReachedCategoryLimit}
          onClick={() => fieldArray?.append({ name: '', categoryId: 0 })}
        >
          <AddNew24 /> Add category
        </AddCategoryButton>
      </StyledTooltip>
    </BodyWrapper>
  );
};
